import { DownloadOutlined, EditOutlined, ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Col, DatePicker, Flex, Form, Input, Modal, notification, Row, Select, Space, Table, TableColumnsType, Tag, Typography } from "antd";
import { useDeletePriceListMutation, useGetAllPriceListQuery, useLazyGetAllPriceListQuery } from "api/priceListApi";
import { OBJ_COLOR } from "commons/constants";
import { colorStatusPriceList, dateFormat, dateFormatServer, numberFormat, translationStatusPriceList } from "commons/constants/util";
import dayjs from "dayjs";
import { useExportExcel } from "hooks/useExportExcel";
import { Pagination } from "pages/categories";
import { useEffect, useMemo, useState } from "react";
import { EnumStatusPriceList, PriceListResponse } from "types/price-list.type";
import ImportExcelPriceListButton from "./components/ImportExcelPriceListButton";
import { PriceListModal } from "./components/PriceListModal";

const { RangePicker } = DatePicker;

interface FilterForm {
	search: string;
	date: dayjs.Dayjs[];
	status: keyof typeof EnumStatusPriceList;
}

const PriceListPage = () => {
	const [form] = Form.useForm();

	const [modal, contextHolder] = Modal.useModal();

	const [priceList, setPriceList] = useState<PriceListResponse>();

	const [page, setPage] = useState({ pageNumber: 1, pageSize: 20 });

	const [pagination, setPagination] = useState<Pagination>();

	const [title, setTitle] = useState<string>("");

	const [open, setOpen] = useState(false);

	const [failed, setFailed] = useState<string[]>([]);

	const [params, setParams] = useState<FilterForm | null>({ search: "", date: [], status: EnumStatusPriceList.valid });

	const { exportExcel } = useExportExcel();

	const paramApi = useMemo(() => {
		const query = {
			...page,
			search: params?.search,
			status: params?.status,
			validFrom: params?.date?.[0]?.toISOString(),
			validTo: params?.date?.[1]?.toISOString(),
		};

		return {
			...query,
		};
	}, [page, params]);

	const { data, isFetching } = useGetAllPriceListQuery({ ...paramApi });

	const [getAllPriceList] = useLazyGetAllPriceListQuery();

	const [deletePriceList] = useDeletePriceListMutation();

	useEffect(() => {
		if (!data?.items) return;

		setPagination({
			current: page.pageNumber,
			pageSize: page.pageSize,
			total: data.totalCount,
			showSizeChanger: true,
		});
	}, [data, page]);

	const handleOnChangePagination = (value: any) => {
		setPage({ pageNumber: value.current, pageSize: value.pageSize });
	};

	const onOpen = () => setOpen(true);

	const onClose = () => {
		setPriceList(undefined);
		setOpen(false);
	};

	const handleOnClickCreate = () => {
		onOpen();
		setTitle("Tạo bảng giá");
	};

	const handleOnClickEdit = (record: PriceListResponse) => {
		setPriceList(record);
		setTitle(`Cập nhật bảng giá ${record.priceListCode}`);
		onOpen();
	};

	const handleOnClickDelete = (record: PriceListResponse) => {
		showDeleteConfirm(record);
	};

	const showDeleteConfirm = (record: PriceListResponse) => {
		modal.confirm({
			title: "Thông báo",
			icon: <ExclamationCircleFilled />,
			content: (
				<Flex gap={4}>
					<Typography.Text>Bạn muốn xoá bảng giá</Typography.Text>
					<Typography.Text strong style={{ color: OBJ_COLOR.SUCCESS }}>
						{record.priceListCode}
					</Typography.Text>
					<Typography.Text>?</Typography.Text>
				</Flex>
			),
			okText: "Đồng ý",
			cancelText: "Huỷ bỏ",
			async onOk() {
				try {
					await deletePriceList(record.id).unwrap();

					notification.success({
						message: "Thành công.",
					});
				} catch (error: any) {
					notification.error({
						message: error?.data?.message,
					});
				}
			},
		});
	};

	const handleOnClickExport = async () => {
		try {
			const result = await getAllPriceList({ ...paramApi, pageSize: 1000 }).unwrap();

			const Heading: any = [
				{
					stt: "STT",
					priceListCode: "ID Bảng giá",
					customerCode: "Mã KH",
					customerName: "Tên KH",
					farmCode: "Mã trại",
					farmName: "Tên trại",
					productCode: "Mã sản phẩm",
					productName: "Tên sản phẩm",
					chickenType: "Gà loại",
					gender: "Giới tính",
					uom: "ĐVT",
					priceAmount: "Giá",
					currency: "Tiền tệ",
					validFrom: "Hiệu lực từ ngày",
					validTo: "Đến ngày",
					status: "Trạng thái",
				},
			];
			const wscols: any = Object.keys(Heading[0]).map(() => ({ wch: 15 }));
			const header: any[] = [];

			const dataExport = result.items.map((item, index) => {
				const { priceListCode, customerCode, customerName, farmCode, farmName, productCode, productName, chickenType, gender, uom, priceAmount, currency, validFrom, validTo, status } = item;

				return {
					stt: index + 1,
					priceListCode,
					customerCode,
					customerName,
					farmCode,
					farmName,
					productCode,
					productName,
					chickenType,
					gender,
					uom,
					priceAmount,
					currency,
					validFrom: dateFormat(validFrom, true),
					validTo: dateFormat(validTo, true),
					status: translationStatusPriceList(status),
				};
			});
			exportExcel({ Heading, wscols, header, dataExport, nameFile: `${dateFormatServer(dayjs())}_price_list` });
		} catch (error) {}
	};

	const columns: TableColumnsType<PriceListResponse> = [
		{
			title: "STT",
			dataIndex: "key",
			ellipsis: true,
		},
		{
			title: "ID Bảng giá",
			dataIndex: "priceListCode",
			ellipsis: true,
			render: (priceListCode: string) => (
				<Typography.Text strong style={{ color: OBJ_COLOR.SUCCESS }}>
					{priceListCode}
				</Typography.Text>
			),
		},
		{
			title: "Mã KH",
			dataIndex: "customerCode",
			ellipsis: true,
			render: (_, record) => `[${record.customerCode}]: ${record.customerName}`,
		},
		{
			title: "Trại",
			dataIndex: "farmCode",
			ellipsis: true,
			render: (_, record) => `[${record.farmCode}]: ${record.farmName}`,
		},
		{
			title: "Mã sản phẩm",
			dataIndex: "productCode",
			ellipsis: true,
			render: (_, record) => `[${record.productCode}]: ${record.productName}`,
		},
		{
			title: "Gà loại",
			dataIndex: "chickenType",
			ellipsis: true,
		},
		{
			title: "Giới tính",
			dataIndex: "gender",
			ellipsis: true,
		},
		{
			title: "ĐVT",
			dataIndex: "uom",
			ellipsis: true,
		},
		{
			title: "Giá",
			dataIndex: "priceAmount",
			ellipsis: true,
			render: (priceAmount: number) => numberFormat(priceAmount),
		},
		{
			title: "Tiền tệ",
			dataIndex: "currency",
			ellipsis: true,
		},
		{
			title: "Hiệu lực từ ngày",
			dataIndex: "validFrom",
			ellipsis: true,
			render: (validFrom: string) => dayjs(validFrom).format("DD/MM/YYYY HH:mm"),
		},
		{
			title: "Đến ngày",
			dataIndex: "validTo",
			ellipsis: true,
			render: (validTo: string) => dayjs(validTo).format("DD/MM/YYYY HH:mm"),
		},
		{
			title: "Hiệu lực",
			dataIndex: "status",
			ellipsis: true,
			fixed: "right",
			render: (status: keyof typeof EnumStatusPriceList) => <Tag color={colorStatusPriceList(status)}>{translationStatusPriceList(status)}</Tag>,
		},
		{
			title: "Trạng thái",
			dataIndex: "isInactive",
			ellipsis: true,
			fixed: "right",
			render: (isInactive: boolean) => <Tag color={isInactive ? "red" : "green"}>{isInactive ? "Ngừng hoạt động" : "Hoạt động"}</Tag>,
		},
		{
			title: "Thao tác",
			dataIndex: "status",
			ellipsis: true,
			fixed: "right",
			render: (_, record) => {
				return (
					<Flex gap={10}>
						{!record.isInactive && record.status === "valid" && <EditOutlined onClick={() => handleOnClickEdit(record)} style={{ color: OBJ_COLOR.INFO, fontSize: 18 }} />}
						{/* <DeleteOutlined onClick={() => handleOnClickDelete(record)} style={{ color: OBJ_COLOR.DANGER, fontSize: 18 }} /> */}
					</Flex>
				);
			},
		},
	];

	const handleOnFinish = (values: FilterForm) => {
		setParams({
			search: values.search,
			date: values.date,
			status: values.status,
		});
		setPage((prev) => ({ ...prev, pageNumber: 1 }));
	};

	const handleReset = () => {
		form.resetFields();
		setParams(null);
	};

	return (
		<>
			<Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
				<Col span={24}>
					<Form form={form} layout="vertical" onFinish={handleOnFinish} disabled={isFetching}>
						<Row gutter={[10, 10]} align="bottom">
							<Col xs={12} xl={4}>
								<Form.Item label="Tìm kiếm" name="search">
									<Input placeholder="Mã khách hàng, tên khách hàng, mã sản phẩm, tên sản phẩm, mã trại, tên trại" />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item label="Ngày hiệu lực" name="date" initialValue={params?.date}>
									<RangePicker placeholder={["Từ ngày", "Đến ngày"]} style={{ width: "100%" }} showTime={{ format: "HH:mm" }} format={"DD-MM-YYYY HH:mm"} />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item label="Trạng thái" name="status" initialValue={params?.status}>
									<Select
										placeholder="Chọn trạng thái"
										style={{ width: "100%" }}
										allowClear
										defaultValue={EnumStatusPriceList.valid}
										options={Object.keys(EnumStatusPriceList).map((key) => ({ label: translationStatusPriceList(key as keyof typeof EnumStatusPriceList), value: key }))}
									/>
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item>
									<Button style={{ width: "100%" }} type="primary" htmlType="submit" loading={isFetching}>
										Search
									</Button>
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item>
									<Button onClick={handleReset} disabled={isFetching} style={{ width: "100%", background: OBJ_COLOR.WARNING }} type="primary">
										Reset
									</Button>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Col>
				<Col span={24}>
					<Flex gap={10}>
						<Button onClick={handleOnClickExport} disabled={isFetching} type="default" icon={<DownloadOutlined />}>
							Export
						</Button>

						<ImportExcelPriceListButton disabled={isFetching} setFailed={setFailed} />

						<Button onClick={handleOnClickCreate} disabled={isFetching} style={{ background: OBJ_COLOR.SUCCESS }} type="primary" icon={<PlusOutlined />}>
							Tạo mới
						</Button>
					</Flex>
				</Col>
				{failed.length > 0 && (
					<Col span={24}>
						<Alert
							closable
							onClose={() => setFailed([])}
							type="error"
							showIcon
							message="Các dòng bị lỗi khi import Excel"
							description={
								<Space direction="vertical" size={4}>
									{failed.map((error, index) => (
										<Typography.Text key={index}>{error}</Typography.Text>
									))}
								</Space>
							}
						/>
					</Col>
				)}
			</Row>
			<Table
				loading={isFetching}
				columns={columns}
				dataSource={data?.items.map((item, key) => ({ ...item, key: key + 1 }))}
				scroll={{ x: true }}
				pagination={pagination}
				onChange={handleOnChangePagination}
			/>
			<PriceListModal priceList={priceList} title={title} open={open} onClose={onClose} />
			{contextHolder}
		</>
	);
};

export default PriceListPage;
